export class RangeSpan {
  static Unbound = {
    Index: Number.NEGATIVE_INFINITY,
    Count: Number.POSITIVE_INFINITY,
    EndIndex: Number.POSITIVE_INFINITY,
  };

  static getUnboundSpan() {
    return new RangeSpan({});
  }

  static indexToLetter(index) {
    if (RangeSpan.Unbound.Index === index) {
      return '';
    }

    let result = '';
    let colIndex = index + 1;
    while (colIndex > 0) {
      const modulo = (colIndex - 1) % 26;
      colIndex = (colIndex - modulo - 1) / 26;
      result = `${String.fromCharCode(modulo + 65)}${result}`;
    }
    return result;
  }

  static indexToNumber(index) {
    return RangeSpan.Unbound.Index == index ? '' : (index + 1).toString();
  }

  get endIndex() { return this.isUnbound ? RangeSpan.Unbound.EndIndex : this.index + this.count - 1; }
  get isUnbound() { return this.index === RangeSpan.Unbound.Index || this.count === RangeSpan.Unbound.Count; }

  constructor({ index = RangeSpan.Unbound.Index, count, endIndex = RangeSpan.Unbound.EndIndex }) {
    this.index = index;
    this.count = count ?? (endIndex === RangeSpan.Unbound.EndIndex ? RangeSpan.Unbound.Count : endIndex - index + 1);
  }

  asLetters() {
    return {
      start: RangeSpan.indexToLetter(this.index),
      end: RangeSpan.indexToLetter(this.endIndex)
    };
  }

  asNumbers() {
    return {
      start: RangeSpan.indexToNumber(this.index),
      end: RangeSpan.indexToNumber(this.endIndex)
    };
  }

  isIntersected(another) {
    return this.index <= another.endIndex && this.endIndex >= another.index;
  }

  getFirstIndexSpan() {
    return this.getResizedSpan(1, true);
  }

  getLastIndexSpan() {
    return this.getOffsetAndResizedSpan({ offset: this.count - 1, delta: 1 }, { useAbsoluteDelta: true });
  }

  getBoundingSpan(another) {
    return new RangeSpan({
      index: Math.min(this.index, another.index),
      endIndex: Math.max(this.endIndex, another.endIndex), 
    });
  }

  getOffsetSpan(offset, useAbsoluteOffset = false) {
    return this.getOffsetAndResizedSpan({ offset }, { useAbsoluteOffset });
  }

  getResizedSpan(delta, useAbsoluteDelta = false) {
    return this.getOffsetAndResizedSpan({ delta }, { useAbsoluteDelta });
  }

  toRelativeIndex(absoluteIndex) {
    return this.isUnbound ? RangeSpan.Unbound.Index : absoluteIndex - this.index;
  }

  toAbsoluteIndex(relativeIndex) {
    return this.isUnbound ? RangeSpan.Unbound.Index : this.index + relativeIndex;
  }

  getOffsetAndResizedSpan(deltas = {}, options = {}) {
    const PINF = Number.POSITIVE_INFINITY;
    const { offset = PINF, delta = PINF } = deltas;
    const { useAbsoluteOffset = false, useAbsoluteDelta = false } = options;
    
    const getValue = (initialValue, deltaValue, useAbsolute) =>
      this.isUnbound
        ? initialValue
        : initialValue + (deltaValue !== PINF ? (useAbsolute ? -(initialValue - deltaValue) : deltaValue) : 0);

    return new RangeSpan({
      index: getValue(this.index, offset, useAbsoluteOffset),
      count: getValue(this.count, delta, useAbsoluteDelta),
    });
  }

  intersect(another) {
    return this.isIntersected(another)
      ? new RangeSpan({
        index: Math.max(this.index, another.index),
        endIndex: Math.min(this.endIndex, another.endIndex)
      })
      : null;
  }
}